/**
 * YouTube IFrame Player Manager
 *
 * Wraps the YouTube IFrame Player API: plays audio via an off-screen iframe —
 * runs in the user's browser, no server-side yt-dlp extraction needed.
 *
 * `ytPlayerManager` is the app's music player (audioService). The mixer makes
 * one more per channel (mixMusic.ts), each with its own iframe: every
 * instance has its own host element ids, the API script loads once for all
 * of them ({@link loadYouTubeIframeApi}), and {@link YouTubePlayerManager.destroy}
 * removes an instance's iframe.
 */

/** The IFrame API, loaded once for every player (null until asked for, or after a failed load). */
let iframeApi: Promise<void> | null = null;

/** How long the IFrame API script may take before a load gives up. */
const IFRAME_API_TIMEOUT_MS = 10_000;

/** Load the YouTube IFrame API script once; resolves when `YT.Player` exists. A failed load can be retried. */
export function loadYouTubeIframeApi(): Promise<void> {
  if (iframeApi) return iframeApi;
  iframeApi = new Promise<void>((resolve, reject) => {
    if (window.YT && window.YT.Player) {
      resolve();
      return;
    }
    const timer = setTimeout(() => reject(new Error('YouTube IFrame API load timeout')), IFRAME_API_TIMEOUT_MS);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      clearTimeout(timer);
      resolve();
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error('Failed to load YouTube IFrame API'));
    };
    document.head.appendChild(script);
  });
  iframeApi.catch(() => { iframeApi = null; });
  return iframeApi;
}

/** yt-dlp proxy URLs by video id (they expire server-side; kept for a while, a few at a time). */
const audioUrlCache = new Map<string, { url: string; ts: number }>();
const AUDIO_URL_CACHE_TTL_MS = 10 * 60 * 1000;
const AUDIO_URL_CACHE_MAX = 20;

/**
 * A YouTube track's audio stream through the server's yt-dlp proxy (for when
 * the IFrame player can't play it: embedding disabled, or a recording needs a
 * capturable element). Cached for a while.
 */
export async function fetchYouTubeAudioUrl(videoId: string, signal?: AbortSignal): Promise<string> {
  const cached = audioUrlCache.get(videoId);
  if (cached && Date.now() - cached.ts < AUDIO_URL_CACHE_TTL_MS) return cached.url;
  const res = await fetch(`/api/youtube/audio/${encodeURIComponent(videoId)}`, { credentials: 'include', signal });
  if (!res.ok) throw new Error('Failed to get audio URL');
  const data = await res.json();
  if (audioUrlCache.size >= AUDIO_URL_CACHE_MAX) audioUrlCache.delete(audioUrlCache.keys().next().value!);
  audioUrlCache.set(videoId, { url: data.url, ts: Date.now() });
  return data.url;
}

export class YouTubePlayerManager {
  private player: YT.Player | null = null;
  private container: HTMLDivElement | null = null;
  private apiReady: Promise<void> | null = null;
  private playerReady: Promise<void> | null = null;
  private playerReadyResolve: (() => void) | null = null;
  private _onEnded: (() => void) | null = null;
  private _looping = true;
  private _loadGen = 0;
  private _currentVideoId: string | null = null;
  private destroyed = false;

  /**
   * `hostId` and `playerId` are the ids of this player's off-screen host and
   * the element the iframe replaces: unique per instance.
   */
  constructor(
    private readonly hostId = 'yt-player-host',
    private readonly playerId = 'yt-iframe-player',
  ) {}

  /** Load the IFrame API script and wait for it to be ready. */
  ensureReady(): Promise<void> {
    if (this.apiReady) return this.apiReady;
    // Create off-screen container
    this.container = document.createElement('div');
      Object.assign(this.container.style, {
        position: 'fixed',
        bottom: '-200px',
        left: '-200px',
        width: '200px',
        height: '200px',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: '-1',
      });
    this.container.id = this.hostId;
    const playerDiv = document.createElement('div');
    playerDiv.id = this.playerId;
    this.container.appendChild(playerDiv);
    document.body.appendChild(this.container);

    this.apiReady = loadYouTubeIframeApi().then(() => {
      if (this.destroyed) throw new Error('The YouTube player was closed');
      this.initPlayer();
    });
    return this.apiReady;
  }

  /** Remove this player and its iframe for good (the app's own player is never destroyed). */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this._loadGen++;
    this._onEnded = null;
    this._onErrorCb = null;
    try {
      this.player?.destroy?.();
    } catch {
      // the iframe is already gone
    }
    this.player = null;
    this.container?.remove();
    this.container = null;
  }

  private initPlayer(): void {
    this.playerReady = new Promise<void>((resolve) => {
      this.playerReadyResolve = resolve;
    });

    this.player = new YT.Player(this.playerId, {
      width: 200,
      height: 200,
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        fs: 0,
        rel: 0,
      },
      events: {
        onReady: () => {
          this.playerReadyResolve?.();
          this.playerReadyResolve = null;
        },
        onStateChange: (e) => this.handleStateChange(e),
        onError: (e) => this.handleError(e),
      },
    });
  }

  private handleStateChange(event: YT.OnStateChangeEvent): void {
    if (event.data === YT.PlayerState.ENDED) {
      if (this._looping) {
        this.player?.seekTo(0, true);
        this.player?.playVideo();
      } else {
        this._onEnded?.();
      }
    }
  }

  private _onErrorCb: ((code: number) => void) | null = null;

  private handleError(event: YT.OnErrorEvent): void {
    console.warn('[ytPlayerManager] player error:', event.data);
    this._onErrorCb?.(event.data);
  }

  /** Cue a video (does not autoplay). Resolves when CUED or ready. */
  async loadVideo(videoId: string): Promise<void> {
    const gen = ++this._loadGen;
    await this.playerReady;
    if (gen !== this._loadGen) return;

    this._currentVideoId = videoId;

    return new Promise<void>((resolve, reject) => {
      if (gen !== this._loadGen) { resolve(); return; }

      const onState = (e: YT.OnStateChangeEvent) => {
        if (gen !== this._loadGen) { cleanup(); resolve(); return; }
        if (e.data === YT.PlayerState.CUED || e.data === YT.PlayerState.PLAYING) {
          cleanup();
          resolve();
        }
      };

      const onError = (code: number) => {
        if (gen !== this._loadGen) { cleanup(); resolve(); return; }
        cleanup();
        // 101/150 = embed-restricted
        reject(new Error(`YouTube embed error: ${code}`));
      };

      // Temporarily intercept state changes
      const origHandleState = this.handleStateChange.bind(this);
      this.handleStateChange = (e: YT.OnStateChangeEvent) => {
        origHandleState(e);
        onState(e);
      };
      this._onErrorCb = onError;

      const cleanup = () => {
        this.handleStateChange = origHandleState;
        this._onErrorCb = null;
      };

      this.player!.cueVideoById(videoId);

      // Timeout — resolve anyway so we don't hang
      setTimeout(() => {
        cleanup();
        if (gen === this._loadGen) resolve();
      }, 8000);
    });
  }

  async play(): Promise<boolean> {
    await this.playerReady;
    try {
      this.player!.playVideo();
      // Wait for PLAYING or BUFFERING (both mean the play command was accepted)
      return new Promise<boolean>((resolve) => {
        let resolved = false;
        const done = (val: boolean) => { if (!resolved) { resolved = true; resolve(val); } };

        const origHandler = this.handleStateChange.bind(this);
        this.handleStateChange = (e: YT.OnStateChangeEvent) => {
          origHandler(e);
          if (e.data === YT.PlayerState.PLAYING || e.data === YT.PlayerState.BUFFERING) {
            this.handleStateChange = origHandler;
            done(true);
          }
        };

        // Also check immediately in case state already changed
        const state = this.player?.getPlayerState();
        if (state === YT.PlayerState.PLAYING || state === YT.PlayerState.BUFFERING) {
          this.handleStateChange = origHandler;
          done(true);
        }

        // Timeout — resolve true anyway since playVideo() was called and didn't throw
        setTimeout(() => {
          this.handleStateChange = origHandler;
          done(true);
        }, 3000);
      });
    } catch {
      return false;
    }
  }

  pause(): void {
    this.player?.pauseVideo?.();
  }

  stop(): void {
    this.player?.stopVideo?.();
  }

  seekTo(seconds: number): void {
    this.player?.seekTo?.(seconds, true);
  }

  setLoop(loop: boolean): void {
    this._looping = loop;
  }

  setVolume(volume: number): void {
    this.player?.setVolume(volume);
  }

  get currentTime(): number {
    return this.player?.getCurrentTime() ?? 0;
  }

  get duration(): number {
    return this.player?.getDuration() ?? 0;
  }

  get isBuffering(): boolean {
    return this.player?.getPlayerState() === YT.PlayerState.BUFFERING;
  }

  get isEnded(): boolean {
    return this.player?.getPlayerState() === YT.PlayerState.ENDED;
  }

  /** Whether this instance made its iframe (the IFrame API loaded and the player was built). */
  get hasIframe(): boolean {
    return this.player !== null;
  }

  get isPlaying(): boolean {
    return this.player?.getPlayerState() === YT.PlayerState.PLAYING;
  }

  get currentVideoId(): string | null {
    return this._currentVideoId;
  }

  set onEnded(cb: (() => void) | null) {
    this._onEnded = cb;
  }

  set onError(cb: ((code: number) => void) | null) {
    this._onErrorCb = cb;
  }
}

export const ytPlayerManager = new YouTubePlayerManager();
